import { useProvinceAnalytics } from "@/hooks/useProvinceAnalytics";
import { useCallback, useEffect } from "react";

/**
 * Conversion tracking for Argentina provinces
 * Tracks different types of conversions with province context
 */
export const ProvinceConversionTracker = () => {
  const { trackConversion, trackCTAClick, trackFormSubmission, isTracking } = useProvinceAnalytics();

  // Track payment-related conversions
  const trackPaymentConversion = useCallback((
    paymentMethod: string,
    amount: number,
    currency: string = 'ARS',
    productType: string
  ) => {
    if (isTracking) {
      trackConversion('payment_completed', amount, currency);
      trackCTAClick('payment_button', `${paymentMethod}_checkout`);
      
      // Additional payment tracking
      trackFormSubmission('payment_form', {
        payment_method: paymentMethod,
        amount,
        currency,
        product_type: productType,
        conversion_funnel_step: 'payment_completed'
      });
    }
  }, [trackConversion, trackCTAClick, trackFormSubmission, isTracking]);

  // Track subscription conversions
  const trackSubscriptionConversion = useCallback((
    subscriptionType: string,
    monthlyValue: number,
    currency: string = 'ARS'
  ) => {
    if (isTracking) {
      trackConversion('subscription_started', monthlyValue, currency);
      
      trackFormSubmission('subscription_form', {
        subscription_type: subscriptionType,
        monthly_value: monthlyValue,
        currency,
        conversion_funnel_step: 'subscription_completed'
      });
    }
  }, [trackConversion, trackFormSubmission, isTracking]);

  // Track lead generation conversions
  const trackLeadConversion = useCallback((
    leadType: string,
    leadSource: string,
    leadValue?: number
  ) => {
    if (isTracking) {
      trackConversion('lead_generated', leadValue);
      
      trackFormSubmission('lead_form', {
        lead_type: leadType,
        lead_source: leadSource,
        lead_value: leadValue,
        conversion_funnel_step: 'lead_captured'
      });
    }
  }, [trackConversion, trackFormSubmission, isTracking]);

  // Track email signup conversions
  const trackEmailSignupConversion = useCallback((
    signupSource: string,
    incentive?: string
  ) => {
    if (isTracking) {
      trackConversion('email_signup');
      
      trackFormSubmission('email_signup_form', {
        signup_source: signupSource,
        incentive,
        conversion_funnel_step: 'email_captured'
      });
    }
  }, [trackConversion, trackFormSubmission, isTracking]);

  // Track social media conversions
  const trackSocialConversion = useCallback((
    platform: string,
    action: string,
    followerCount?: number
  ) => {
    if (isTracking) {
      trackConversion('social_follow');
      
      trackCTAClick(`${platform}_follow_button`, `social_${platform}`);
      
      trackFormSubmission('social_conversion', {
        platform,
        action,
        follower_count: followerCount,
        conversion_funnel_step: 'social_follow_completed'
      });
    }
  }, [trackConversion, trackCTAClick, trackFormSubmission, isTracking]);

  // Track content engagement conversions
  const trackContentConversion = useCallback((
    contentType: string,
    contentId: string,
    engagementLevel: 'low' | 'medium' | 'high'
  ) => {
    if (isTracking) {
      trackConversion('content_engagement');
      
      trackFormSubmission('content_interaction', {
        content_type: contentType,
        content_id: contentId,
        engagement_level: engagementLevel,
        conversion_funnel_step: 'content_consumed'
      });
    }
  }, [trackConversion, trackFormSubmission, isTracking]);

  // Expose conversion tracking functions globally for easy access
  useEffect(() => {
    if (isTracking && typeof window !== 'undefined') {
      // Make conversion tracking functions available globally
      (window as unknown as Record<string, unknown>).provinceConversions = {
        trackPayment: trackPaymentConversion,
        trackSubscription: trackSubscriptionConversion,
        trackLead: trackLeadConversion,
        trackEmailSignup: trackEmailSignupConversion,
        trackSocial: trackSocialConversion,
        trackContent: trackContentConversion
      };

      // Clean up on unmount
      return () => {
        delete (window as unknown as Record<string, unknown>).provinceConversions;
      };
    }
  }, [
    isTracking,
    trackPaymentConversion,
    trackSubscriptionConversion,
    trackLeadConversion,
    trackEmailSignupConversion,
    trackSocialConversion,
    trackContentConversion
  ]);

  // This component doesn't render anything
  return null;
};

/**
 * HOC for wrapping components with conversion tracking
 */
export function withProvinceConversionTracking<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  conversionType: string,
  conversionData?: Record<string, unknown>
) {
  return function ConversionTrackedComponent(props: P) {
    const { trackConversion, isTracking } = useProvinceAnalytics();

    useEffect(() => {
      if (isTracking) {
        trackConversion(conversionType, undefined, undefined);
      }
    }, [trackConversion, isTracking]);

    return <WrappedComponent {...props} />;
  };
}

/**
 * Button component with built-in conversion tracking
 */
export const ConversionButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  conversionType: string;
  conversionValue?: number;
  currency?: string;
  className?: string;
  disabled?: boolean;
}> = ({
  children,
  onClick,
  conversionType,
  conversionValue,
  currency,
  className = "",
  disabled = false
}) => {
  const { trackConversion, trackCTAClick, isTracking } = useProvinceAnalytics();

  const handleClick = () => {
    if (isTracking && !disabled) {
      trackConversion(conversionType, conversionValue, currency);
      trackCTAClick(`conversion_button_${conversionType}`, 'conversion_flow');
    }
    onClick?.();
  };

  return (
    <button
      onClick={handleClick}
      className={className}
      disabled={disabled}
    >
      {children}
    </button>
  );
};